<template>
	<div class="virtual-transfer-demo">
		<div class="demo-header">
			<h2>虚拟滚动Transfer组件演示</h2>
			<p>展示5000+数据量的性能优化效果</p>
		</div>

		<el-card class="demo-controls">
			<h3>测试控制</h3>
			<el-row :gutter="20">
				<el-col :span="8">
					<el-button type="primary" @click="generateTestData(1000)">生成1000条数据</el-button>
				</el-col>
				<el-col :span="8">
					<el-button type="primary" @click="generateTestData(5000)">生成5000条数据</el-button>
				</el-col>
				<el-col :span="8">
					<el-button type="primary" @click="generateTestData(10000)">生成10000条数据</el-button>
				</el-col>
			</el-row>
			<el-row style="margin-top: 15px">
				<el-col :span="12">
					<el-button @click="selectRandom">随机选择500项</el-button>
				</el-col>
				<el-col :span="12">
					<el-button @click="clearSelection">清空选择</el-button>
				</el-col>
			</el-row>
		</el-card>

		<el-card class="demo-stats">
			<h3>性能统计</h3>
			<el-row :gutter="20">
				<el-col :span="6">
					<div class="stat-item">
						<span class="stat-label">总数据量:</span>
						<span class="stat-value">{{ totalCount }}</span>
					</div>
				</el-col>
				<el-col :span="6">
					<div class="stat-item">
						<span class="stat-label">已选择:</span>
						<span class="stat-value">{{ selectedCount }}</span>
					</div>
				</el-col>
				<el-col :span="6">
					<div class="stat-item">
						<span class="stat-label">渲染时间:</span>
						<span class="stat-value">{{ renderTime }}ms</span>
					</div>
				</el-col>
				<el-col :span="6">
					<div class="stat-item">
						<span class="stat-label">DOM节点:</span>
						<span class="stat-value">~26个</span>
					</div>
				</el-col>
			</el-row>
		</el-card>

		<el-card class="demo-content">
			<h3>虚拟滚动Transfer组件</h3>
			<virtual-transfer
				v-model="selectedItems"
				:data="testData"
				:props="transferProps"
				:titles="['可选渠道(' + unselectedCount + ')', '已选渠道(' + selectedCount + ')']"
				:button-texts="['移除', '选择']"
				filterable
				@change="handleTransferChange"></virtual-transfer>
		</el-card>

		<el-card v-if="performanceLogs.length > 0" class="demo-logs">
			<h3>性能日志</h3>
			<el-table :data="performanceLogs" style="width: 100%">
				<el-table-column prop="timestamp" label="时间" width="180"></el-table-column>
				<el-table-column prop="action" label="操作" width="120"></el-table-column>
				<el-table-column prop="dataCount" label="数据量" width="100"></el-table-column>
				<el-table-column prop="renderTime" label="渲染时间(ms)" width="120"></el-table-column>
				<el-table-column prop="selectedCount" label="选中数量" width="100"></el-table-column>
				<el-table-column prop="memoryUsage" label="内存使用" width="120"></el-table-column>
			</el-table>
		</el-card>
	</div>
</template>

<script>
import VirtualTransfer from '@/components/common/VirtualTransfer'

export default {
	name: 'VirtualTransferDemo',
	components: {
		VirtualTransfer,
	},
	data() {
		return {
			// 测试数据
			testData: [],
			// 选中的项目
			selectedItems: [],
			// 渲染时间
			renderTime: 0,
			// 传输组件配置
			transferProps: {
				key: 'id',
				label: 'name',
			},
			// 性能日志
			performanceLogs: [],
		}
	},
	computed: {
		/**
		 * 总数据量
		 * @returns {number}
		 */
		totalCount() {
			return this.testData.length
		},

		/**
		 * 已选择数量
		 * @returns {number}
		 */
		selectedCount() {
			return this.selectedItems.length
		},

		/**
		 * 未选择数量
		 * @returns {number}
		 */
		unselectedCount() {
			return this.totalCount - this.selectedCount
		},
	},
	mounted() {
		// 默认生成1000条测试数据
		this.generateTestData(1000)
	},
	methods: {
		/**
		 * 生成测试数据
		 * @param {number} count - 数据数量
		 */
		generateTestData(count) {
			const startTime = performance.now()

			// 生成测试数据
			const data = []
			for (let i = 1; i <= count; i++) {
				data.push({
					id: i,
					name: `渠道${i.toString().padStart(5, '0')} - ${this.getRandomChannelType()}`,
					type: this.getRandomChannelType(),
					region: this.getRandomRegion(),
					status: Math.random() > 0.5 ? 'active' : 'inactive',
				})
			}

			this.testData = data
			this.selectedItems = []

			const endTime = performance.now()
			this.renderTime = Math.round(endTime - startTime)

			// 记录性能日志
			this.addPerformanceLog({
				action: `生成${count}条数据`,
				dataCount: count,
				renderTime: this.renderTime,
				selectedCount: 0,
				memoryUsage: this.getMemoryUsage(),
			})
		},

		/**
		 * 随机选择项目
		 */
		selectRandom() {
			const count = Math.min(500, this.testData.length)
			const randomIndices = []

			// 生成随机索引
			while (randomIndices.length < count) {
				const randomIndex = Math.floor(Math.random() * this.testData.length)
				if (!randomIndices.includes(randomIndex)) {
					randomIndices.push(randomIndex)
				}
			}

			// 选择对应的项目
			this.selectedItems = randomIndices.map((index) => this.testData[index].id)

			// 记录性能日志
			this.addPerformanceLog({
				action: '随机选择',
				dataCount: this.testData.length,
				renderTime: 0,
				selectedCount: count,
				memoryUsage: this.getMemoryUsage(),
			})
		},

		/**
		 * 清空选择
		 */
		clearSelection() {
			this.selectedItems = []

			// 记录性能日志
			this.addPerformanceLog({
				action: '清空选择',
				dataCount: this.testData.length,
				renderTime: 0,
				selectedCount: 0,
				memoryUsage: this.getMemoryUsage(),
			})
		},

		/**
		 * 处理传输变化
		 * @param {Array} value - 新值
		 * @param {string} direction - 方向
		 * @param {Array} movedKeys - 移动的键
		 */
		handleTransferChange(value, direction, movedKeys) {
			console.log('Transfer变化:', {
				direction,
				movedCount: movedKeys.length,
				totalSelected: value.length,
			})

			// 记录性能日志
			this.addPerformanceLog({
				action: `${direction === 'right' ? '选择' : '移除'}${movedKeys.length}项`,
				dataCount: this.testData.length,
				renderTime: 0,
				selectedCount: value.length,
				memoryUsage: this.getMemoryUsage(),
			})
		},

		/**
		 * 添加性能日志
		 * @param {Object} logData - 日志数据
		 */
		addPerformanceLog(logData) {
			this.performanceLogs.unshift({
				timestamp: new Date().toLocaleTimeString(),
				...logData,
			})

			// 只保留最近20条日志
			if (this.performanceLogs.length > 20) {
				this.performanceLogs = this.performanceLogs.slice(0, 20)
			}
		},

		/**
		 * 获取随机渠道类型
		 * @returns {string}
		 */
		getRandomChannelType() {
			const types = ['银行', '保险公司', '券商', '基金', '信托', '第三方销售', 'APP', '官网', '电话销售']
			return types[Math.floor(Math.random() * types.length)]
		},

		/**
		 * 获取随机地区
		 * @returns {string}
		 */
		getRandomRegion() {
			const regions = ['北京', '上海', '广州', '深圳', '杭州', '南京', '成都', '武汉', '西安', '重庆']
			return regions[Math.floor(Math.random() * regions.length)]
		},

		/**
		 * 获取内存使用情况
		 * @returns {string}
		 */
		getMemoryUsage() {
			if (performance.memory) {
				const used = Math.round(performance.memory.usedJSHeapSize / 1024 / 1024)
				return `${used}MB`
			}
			return 'N/A'
		},
	},
}
</script>

<style lang="scss" scoped>
.virtual-transfer-demo {
	padding: 20px;

	.demo-header {
		text-align: center;
		margin-bottom: 20px;

		h2 {
			color: #303133;
			margin-bottom: 5px;
		}

		p {
			color: #606266;
		}
	}

	.demo-controls,
	.demo-stats,
	.demo-content,
	.demo-logs {
		margin-bottom: 20px;

		h3 {
			margin-top: 0;
			color: #303133;
		}
	}

	.stat-item {
		display: flex;
		justify-content: space-between;
		padding: 10px;
		background: #f5f7fa;
		border-radius: 4px;

		.stat-label {
			color: #606266;
		}

		.stat-value {
			font-weight: bold;
			color: #409eff;
		}
	}

	.demo-content {
		::v-deep .virtual-transfer {
			justify-content: center;
		}
	}
}
</style>
